import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// FontAwesome
import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Hooks
import { usePermissionGroupsAndRoles } from 'app/client/utilities/hooks/useCheckPermissionGroups';
import useIsOwner from 'app/client/utilities/hooks/useIsOwner';

// React shared
import Portal from '@exonet/react-shared/lib/Elements/Portal';

// Components
import Feedback from 'app/client/modules/Feedback/lazy';
import MenuItem from './components/MenuItem';
import MenuGroup from './components/MenuGroup';

// Utilities
import getMenuItems from './utilities';

// Styled
import * as S from './styled';

const Menu = forwardRef(({ customer, open }, ref) => {
  const { t } = useTranslation();
  const data = usePermissionGroupsAndRoles();
  const owner = useIsOwner();

  return (
    <S.Menu ref={ref} open={open}>
      <S.Header>
        <S.BrandLink to="/" aria-label="Dashboard">
          <S.Brand />
          <S.BrandLabel>Portal</S.BrandLabel>
        </S.BrandLink>
      </S.Header>
      <ul>
        {getMenuItems(customer.name, data, owner).map(({ title, icon, to, items = [] }) => {
          if (!to) {
            const key = t(`core:menu.items.${title}`, title);

            return <S.MenuCategory key={key}>{key}</S.MenuCategory>;
          }

          if (items.length > 0) {
            return (
              <MenuGroup
                key={to}
                customerId={customer.id}
                title={title}
                icon={icon}
                to={to}
                items={items}
              />
            );
          }

          return <MenuItem key={to} customerId={customer.id} title={title} icon={icon} to={to} />;
        })}
      </ul>
      <S.Footer>
        <Portal
          renderTrigger={({ onOpen }) => (
            <S.FeedbackButton onClick={onOpen}>
              <>
                <FontAwesomeIcon icon={byPrefixAndName.fas.comments} />
                {t('core:menu.provideFeedback')}
              </>
            </S.FeedbackButton>
          )}
          render={({ onClose }) => <Feedback customerId={customer.id} onClose={onClose} />}
        />
      </S.Footer>
    </S.Menu>
  );
});

Menu.displayName = 'Menu';

Menu.propTypes = {
  customer: PropTypes.oneOfType([
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    PropTypes.bool,
  ]).isRequired,
  open: PropTypes.bool.isRequired,
};

export default Menu;
